import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  createPacket,
  findPacketsBySubject,
  listPackets,
  promoteMember,
  addEvidence,
  IntelPacket,
  MemberType,
  PromoteRequest,
} from '../../services/intelPacketApi';

export interface PromoteTarget {
  member_type: MemberType;
  member_id: string;
  label?: string;
  is_primary?: boolean;
  meta?: Record<string, unknown>;
}

export interface PromoteToPacketDialogProps {
  open: boolean;
  onClose: () => void;
  target: PromoteTarget | null;
  extraMembers?: PromoteTarget[];
  defaultName?: string;
  /** When set, also persist a text evidence row after promote. */
  evidence?: { title: string; body: string; kind?: string; source?: string };
  onPromoted?: (packet: IntelPacket) => void;
}

const PromoteToPacketDialog: React.FC<PromoteToPacketDialogProps> = ({
  open,
  onClose,
  target,
  extraMembers = [],
  defaultName,
  evidence,
  onPromoted,
}) => {
  const navigate = useNavigate();
  const [packets, setPackets] = useState<IntelPacket[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'existing' | 'new'>('new');
  const [packetId, setPacketId] = useState<number | ''>('');
  const [name, setName] = useState(defaultName || '');
  const [existingForSubject, setExistingForSubject] = useState<IntelPacket[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listPackets();
      setPackets(rows);
      setMode('new');
      if (rows.length > 0) setPacketId(rows[0].id);
      if (target) {
        const hits = await findPacketsBySubject(target.member_type, target.member_id);
        setExistingForSubject(hits);
      } else {
        setExistingForSubject([]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load packets');
    } finally {
      setLoading(false);
    }
  }, [target]);

  useEffect(() => {
    if (open) {
      setName(defaultName || target?.label || '');
      load();
    }
  }, [open, defaultName, target, load]);

  const attach = async (pid: number) => {
    if (!target) return;
    const members: PromoteRequest[] = [
      {
        member_type: target.member_type,
        member_id: target.member_id,
        label: target.label,
        is_primary: target.is_primary ?? true,
        meta: target.meta,
      },
      ...extraMembers.map((m) => ({
        member_type: m.member_type,
        member_id: m.member_id,
        label: m.label,
        is_primary: false,
        meta: m.meta,
      })),
    ];
    let packet: IntelPacket | null = null;
    for (const m of members) {
      packet = await promoteMember(pid, m);
    }
    if (evidence && packet) {
      await addEvidence(pid, {
        title: evidence.title,
        kind: evidence.kind || 'note',
        source: evidence.source,
        body: evidence.body,
      });
    }
    return packet;
  };

  const handleConfirm = async () => {
    if (!target) return;
    setSaving(true);
    setError(null);
    try {
      let packet: IntelPacket | undefined;
      if (mode === 'new') {
        const created = await createPacket({
          name: name.trim() || target.label || 'Intel Packet',
          primary_subject_type: target.member_type,
          primary_subject_id: target.member_id,
          primary_label: target.label,
        });
        packet = (await attach(created.id)) || created;
      } else if (packetId !== '') {
        packet = (await attach(Number(packetId))) || undefined;
      }
      if (packet) {
        window.dispatchEvent(new Event('refresh-intel-packet'));
        onPromoted?.(packet);
        onClose();
        navigate(`/intel/${packet.id}`);
        return;
      }
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Promote failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Promote to Intel Packet</DialogTitle>
      <DialogContent>
        {target && (
          <Typography variant="body2" sx={{ mb: 1.5 }} color="text.secondary">
            {target.label || target.member_id} ({target.member_type.replace('_', ' ')})
          </Typography>
        )}
        {existingForSubject.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1, bgcolor: 'rgba(245,185,66,0.08)', border: '1px solid rgba(245,185,66,0.35)' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              Already on file
            </Typography>
            {existingForSubject.slice(0, 3).map((p) => (
              <Button
                key={p.id}
                size="small"
                onClick={() => { onClose(); navigate(`/intel/${p.id}`); }}
                sx={{ textTransform: 'none', display: 'block' }}
              >
                Open {p.name}
              </Button>
            ))}
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 1.5, mt: 0.5 }}>
              <InputLabel>Destination</InputLabel>
              <Select
                label="Destination"
                value={mode}
                onChange={(e) => setMode(e.target.value as 'existing' | 'new')}
              >
                <MenuItem value="new">Create new packet</MenuItem>
                <MenuItem value="existing" disabled={packets.length === 0}>
                  Add to existing packet
                </MenuItem>
              </Select>
            </FormControl>
            {mode === 'new' ? (
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Packet name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel>Packet</InputLabel>
                <Select
                  label="Packet"
                  value={packetId}
                  onChange={(e) => setPacketId(e.target.value as number)}
                >
                  {packets.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} ({p.status})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        )}
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={saving || loading || !target}>
          {saving ? 'Promoting…' : 'Promote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromoteToPacketDialog;
